<script lang="ts">
import type { Card } from '$types/card';
import CalcCard from './CalcCard.svelte';
import ClozeCard from './ClozeCard.svelte';
import ImageCard from './ImageCard.svelte';
import QaCard from './QaCard.svelte';

interface Props {
  card: Card;
  revealed: boolean;
  position: number;
  total: number;
  occlusionIndex?: number;
  fadeLevel?: number;
}

const { card, revealed, position, total, occlusionIndex = 0, fadeLevel = 0 }: Props = $props();

const TYPE_LABELS: Record<string, string> = {
  cloze: 'lückentext',
  qa: 'frage·antwort',
  calc: 'rechnung',
  faded: 'rechnung·faded',
  vocab: 'vokabel',
  image: 'bild·okklusion',
};

function pad3(n: number): string {
  return String(Math.max(0, n)).padStart(3, '0');
}
</script>

<article class="cardview" data-card-type={card.type} data-deck={card.deck}>
  <div class="cv-meta">
    <div class="cv-meta-l">
      <span class="cv-deck">{card.deck}</span>
      <span class="cv-sep">│</span>
      <span class="cv-type">{TYPE_LABELS[card.type] ?? card.type}</span>
    </div>
    <div class="cv-meta-r">
      <span class="cv-pos">{pad3(position)}/{pad3(total)}</span>
    </div>
  </div>

  <div class="cv-cross">
    <span class="ch-tl" aria-hidden="true">┌</span>
    <span class="ch-tr" aria-hidden="true">┐</span>
    <span class="ch-bl" aria-hidden="true">└</span>
    <span class="ch-br" aria-hidden="true">┘</span>
    <div class="cv-body">
      {#if card.type === 'cloze'}
        <ClozeCard {card} {revealed} />
      {:else if card.type === 'qa' || card.type === 'vocab'}
        <QaCard {card} {revealed} />
      {:else if card.type === 'image'}
        <ImageCard {card} {revealed} {occlusionIndex} />
      {:else if card.type === 'calc' || card.type === 'faded'}
        <CalcCard {card} {revealed} {fadeLevel} />
      {/if}
    </div>
  </div>

  {#if card.source}
    <div class="cv-footer">
      <div class="cv-source">► quelle: {card.source}</div>
    </div>
  {/if}
</article>

<style>
  .cardview {
    background: var(--paper);
    border: 2px solid var(--ink);
    position: relative;
    margin-top: 8px;
    box-shadow: 6px 6px 0 var(--ink-3);
  }

  .cv-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 12px;
    background: var(--ink);
    color: var(--paper);
    font-size: 10px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    font-weight: 600;
  }
  .cv-meta-l { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
  .cv-sep { color: var(--muted); }
  .cv-deck { color: var(--paper); }
  .cv-type { color: var(--paper-3); }
  .cv-pos { font-variant-numeric: tabular-nums; font-weight: 700; color: var(--paper); }

  .cv-cross {
    position: relative;
    padding: var(--pad);
    min-height: 220px;
  }
  .ch-tl, .ch-tr, .ch-bl, .ch-br {
    position: absolute;
    color: var(--accent);
    font-size: 14px;
    line-height: 1;
  }
  .ch-tl { top: 4px; left: 4px; }
  .ch-tr { top: 4px; right: 4px; }
  .ch-bl { bottom: 4px; left: 4px; }
  .ch-br { bottom: 4px; right: 4px; }
  .cv-body { position: relative; z-index: 1; }

  .cv-footer {
    border-top: 1px dashed var(--rule);
    padding: 8px var(--pad);
    background: var(--paper-2);
  }
  .cv-source { color: var(--muted); font-size: 10px; letter-spacing: 0.04em; }
</style>
